/**
 * Calculates the remaining term of the mortgage at a point in time
 *
 * @param {number} remainingBalance Remaining balance in pence
 * @param {number} monthlyPayment Monthly payment in pence
 * @param {number} interestRate Interest rate as a float
 * OPTIONAL: Overpayment in pence
 * @param {number} [overpaymentAmount]
 * OPTIONAL: Overpayment interval (monthly / single)
 * @param {number} [overpaymentInterval]
 *
 * @return {number} Remaining term in months
 *
 * ORIGINAL FUNCTION
 * months = [ Log10
 *  ( totalMonthlyPayment /
 *    ( totalMontlyPayment - ( monthlyInterestRate * remainingBalance )
 *  )
 * ] / [ Log10 ( 1 + monthlyInterestRate ) ]
 *
 */
import { round } from '@homamo/meadow-utils';

import getMonthlyInterestRate from './getMonthlyInterestRate';
import defaults from '../../defaults';

const getRemainingTerm = (args) => {
  // Check arguments
  if (!args.remainingBalance && args.remainingBalance !== 0)
    throw new Error('Remaining balance is required');
  if (!args.monthlyPayment && args.monthlyPayment !== 0)
    throw new Error('Monthly payment is required');
  if (!args.interestRate && args.remainingBalance !== 0)
    throw new Error('Interest rate is required');

  const { monthlyPayment, interestRate, overpaymentInterval } = args;

  const overpaymentAmount = args.overpaymentAmount || 0; // Default to 0 if no monthlyOverpayment set

  const remainingBalance =
    args.overpaymentInterval === 'single'
      ? args.remainingBalance - overpaymentAmount
      : args.remainingBalance;

  // Calculate monthly interest rate
  const monthlyInterestRate = getMonthlyInterestRate(interestRate);

  // Calculate monthly payment
  const totalMonthlyPayment =
    overpaymentInterval === 'monthly'
      ? monthlyPayment + overpaymentAmount
      : monthlyPayment;

  // Calculate increase (e.g 103.92%)
  const increase = 1 + monthlyInterestRate;
  const monthInterest = monthlyInterestRate * remainingBalance;

  const months =
    Math.log10(totalMonthlyPayment / (totalMonthlyPayment - monthInterest)) /
    Math.log10(increase);

  return round(months, defaults.decimalDigits);
};

export default getRemainingTerm;
